'use client'

import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  Button,
} from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  cardVariants,
  reducedMotionVariants,
} from '../../lib/motion'
import { CodeWindowBase } from '../../lib/code-window'

interface CarouselFeature {
  title: string
  /** Feature description - supports Markdown (inline code, links, bold, etc.) */
  description: string
  /** Optional button below description */
  button?: {
    text: string
    url: string
  }
  /** Display type for this slide */
  mediaType?: 'image' | 'code'
  /** Image configuration (used when mediaType is 'image') */
  image?: {
    src: string
    alt: string
  }
  /** Code window configuration (used when mediaType is 'code') */
  codeWindow?: {
    code: string
    language: string
    filename?: string
  }
}

/**
 * FeaturesCarousel block props (carousel feature showcase)
 *
 * @remarks
 * **CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Carousel layout with prev/next navigation
 * - Each slide has title, description, and either image or code window
 * - Media fills right side of slide
 * - Each slide can independently be image or code
 * - Best with 3-5 features for good carousel experience
 * - Muted background on each slide
 * - Optional section heading and description above carousel
 *
 * **Markdown Support in Feature Descriptions:**
 * - `**bold**` for emphasis
 * - `\`inline code\`` for code snippets
 * - `- item` for bullet lists
 * - `[text](url)` for links
 * - `\n\n` for paragraph breaks
 */
export interface FeaturesCarouselProps {
  /** Optional section heading above carousel */
  heading?: string
  /** Optional section description below heading */
  description?: string
  /** Feature items to display in carousel */
  features?: CarouselFeature[]
  /** Code window theme (applies to all code slides) */
  codeTheme?: 'dark' | 'light' | 'auto'
  className?: string
}

const defaultFeatures: CarouselFeature[] = [
  {
    title: 'Built for artists and designers',
    description:
      'Create stunning visuals with our **intuitive design tools**. Built from the ground up for creative professionals.',
    mediaType: 'image',
    image: {
      src: '/placeholders/placeholder-1.svg',
      alt: 'Design tools preview',
    },
  },
  {
    title: 'Built for coders and developers',
    description:
      'Type-safe components with full **TypeScript** support. Works with `npm`, `yarn`, `pnpm`, or `bun`.',
    button: {
      text: 'View Docs',
      url: '#',
    },
    mediaType: 'code',
    codeWindow: {
      code: `import { Button } from '@mdxui/primitives'

export function Hero() {
  return (
    <section className="py-32">
      <h1>Welcome to MDXUI</h1>
      <Button>Get Started</Button>
    </section>
  )
}`,
      language: 'tsx',
      filename: 'hero.tsx',
    },
  },
  {
    title: 'Build your own website with MDXUI',
    description:
      'Combine the power of [MDX](https://mdxjs.com/) with beautiful UI components. Create content-rich sites effortlessly.',
    mediaType: 'image',
    image: {
      src: '/placeholders/placeholder-1.svg',
      alt: 'Website builder preview',
    },
  },
]

const FeaturesCarousel = ({
  heading,
  description,
  features = defaultFeatures,
  codeTheme = 'auto',
  className,
}: FeaturesCarouselProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const contentAnimation = prefersReducedMotion ? reducedMotionVariants : cardVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        {/* Optional section header */}
        {(heading || description) && (
          <motion.div variants={contentAnimation} className="mb-12 text-center">
            {heading && (
              <h2 className="text-3xl font-semibold md:text-4xl">{heading}</h2>
            )}
            {description && (
              <div className="mx-auto mt-4 max-w-2xl">
                <Markdown size="base">{description}</Markdown>
              </div>
            )}
          </motion.div>
        )}
        <motion.div variants={contentAnimation}>
          <Carousel className="relative w-full [&_[data-slot=carousel-content]]:rounded-[var(--radius-2xl,calc(var(--radius)*2))]">
            <CarouselContent className="ease-in">
              {features.map((feature) => (
                <CarouselItem key={feature.title}>
                  <div className="flex h-full min-h-[560px] flex-col items-center justify-between gap-4 rounded-[var(--radius-2xl,calc(var(--radius)*2))] bg-muted py-10 pl-4 md:py-20 md:flex-row md:pl-16">
                    <div className="w-full pr-2 md:max-w-md md:pr-0">
                      <h2 className="mb-6 text-xl font-semibold sm:text-2xl md:text-3xl">
                        {feature.title}
                      </h2>
                      <div className="prose prose-sm max-w-none text-muted-foreground prose-headings:text-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline md:prose-base">
                        <ReactMarkdown>{feature.description}</ReactMarkdown>
                      </div>
                      {feature.button && (
                        <Button asChild className="mt-6">
                          <a href={feature.button.url}>{feature.button.text}</a>
                        </Button>
                      )}
                    </div>
                    <div className="h-80 w-full max-w-2xl overflow-hidden md:h-[400px]">
                      {feature.mediaType === 'code' && feature.codeWindow ? (
                        <CodeWindowBase
                          code={feature.codeWindow.code}
                          language={feature.codeWindow.language}
                          filename={feature.codeWindow.filename}
                          showHeader={false}
                          showCopy={false}
                          theme={codeTheme}
                          style="borderless"
                          className="h-full w-full rounded-l-[var(--radius-2xl,calc(var(--radius)*2))]"
                        />
                      ) : feature.image ? (
                        <img
                          src={feature.image.src}
                          alt={feature.image.alt}
                          className="h-full w-full rounded-l-[var(--radius-2xl,calc(var(--radius)*2))] object-cover"
                        />
                      ) : null}
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="absolute left-4 bottom-6 top-auto size-12 translate-y-0 dark:border-border md:left-6" />
            <CarouselNext className="absolute right-4 bottom-6 top-auto size-12 translate-y-0 dark:border-border md:right-6" />
          </Carousel>
        </motion.div>
      </div>
    </motion.section>
  )
}

export { FeaturesCarousel }
